<template>
  <section class="page" data-testid="page-grudges">
    <header class="page-header">
      <div><h1>记仇</h1><p>记录用户行为事件，支持正负向关系分。</p></div>
      <button class="button primary" :disabled="loading" @click="load">{{ loading ? "刷新中..." : "刷新" }}</button>
    </header>
    <div class="metrics-grid">
      <MetricCard label="事件总数" :value="rows.length" />
      <MetricCard label="记恩" :value="favorCount" />
      <MetricCard label="记仇" :value="grudgeCount" />
      <MetricCard label="涉及用户" :value="userCount" />
      <MetricCard label="当前匹配" :value="filteredRows.length" />
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <div class="split">
      <div class="panel">
        <h2>记录列表</h2>
        <div class="toolbar">
          <input v-model="query" placeholder="搜索 QQ、类型、原因" />
          <button class="button" :class="{ primary: filter === 'all' }" @click="filter = 'all'">全部</button>
          <button class="button" :class="{ primary: filter === 'favor' }" @click="filter = 'favor'">记恩</button>
          <button class="button" :class="{ primary: filter === 'grudge' }" @click="filter = 'grudge'">记仇</button>
        </div>
        <DataTable :columns="columns" :rows="filteredRows" row-key="key">
          <template #type="{ row }"><StatusBadge :status="row.type === 'favor' ? 'ok' : 'error'">{{ typeLabel(row.type) }}</StatusBadge></template>
          <template #timestamp="{ row }">{{ formatTime(row.timestamp) }}</template>
          <template #description="{ row }"><pre>{{ row.description }}</pre></template>
          <template #actions="{ row }"><button class="button small danger" :disabled="loading" @click="remove(row.user_id, row.index)">删除</button></template>
        </DataTable>
      </div>
      <div class="panel">
        <h2>新增事件</h2>
        <div class="form-grid">
          <FormField label="用户 QQ"><input v-model="form.user_id" /></FormField>
          <FormField label="类型">
            <select v-model="form.type"><option value="grudge">记仇</option><option value="favor">记恩</option></select>
          </FormField>
          <FormField label="强度"><input v-model.number="form.severity" type="number" min="1" max="5" /></FormField>
        </div>
        <FormField label="原因"><textarea v-model="form.reason" /></FormField>
        <button class="button primary" :disabled="loading || !canAdd" @click="add">{{ loading ? "处理中..." : "添加" }}</button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { api, postJson } from "../api/client";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";
import StatusBadge from "../ui/StatusBadge.vue";

const toast = useToastStore();
const grudges = ref<Record<string, any>>({});
const loading = ref(false);
const error = ref("");
const actionResult = ref("");
const query = ref("");
const filter = ref<"all" | "favor" | "grudge">("all");
const form = reactive({ user_id: "", type: "grudge", severity: 1, reason: "" });
const columns = [
  { key: "user_id", label: "用户" },
  { key: "score", label: "分数" },
  { key: "type", label: "类型" },
  { key: "severity", label: "强度" },
  { key: "timestamp", label: "时间" },
  { key: "description", label: "描述" },
  { key: "actions", label: "操作" }
];
const rows = computed(() => Object.entries(grudges.value).flatMap(([uid, item]) => (item.events || []).map((event: any, index: number) => ({
  key: `${uid}:${index}`,
  user_id: uid,
  index,
  score: item.score,
  ...event
}))));
const favorCount = computed(() => rows.value.filter((row) => row.type === "favor").length);
const grudgeCount = computed(() => rows.value.filter((row) => row.type === "grudge").length);
const userCount = computed(() => Object.keys(grudges.value).length);
const canAdd = computed(() => Boolean(form.user_id && form.reason.trim()));
const filteredRows = computed(() => {
  const text = query.value.trim().toLowerCase();
  return rows.value.filter((row) => {
    if (filter.value !== "all" && row.type !== filter.value) return false;
    if (!text) return true;
    return `${row.user_id} ${row.type} ${typeLabel(row.type)} ${row.description}`.toLowerCase().includes(text);
  });
});
function typeLabel(type: string) {
  return type === "favor" ? "记恩" : type === "grudge" ? "记仇" : type;
}
function formatTime(value: number) {
  const n = Number(value || 0);
  return n ? new Date(n * 1000).toLocaleString() : "-";
}
function errorText(err: unknown, fallback: string) {
  return err instanceof Error ? err.message : fallback;
}
async function load() {
  loading.value = true;
  error.value = "";
  try {
    grudges.value = (await api<{ grudges: Record<string, any> }>("/api/grudges")).grudges || {};
  } catch (err) {
    error.value = errorText(err, "关系事件加载失败");
  } finally {
    loading.value = false;
  }
}
async function runAction(label: string, operation: () => Promise<unknown>) {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await operation();
    actionResult.value = label;
    toast.push(label);
    await load();
  } catch (err) {
    error.value = errorText(err, `${label}失败`);
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function add() {
  if (!canAdd.value) return;
  await runAction("关系事件已添加，并同步到好感度", async () => {
    await postJson("/api/grudge/add", { ...form, user_id: Number(form.user_id) });
    form.reason = "";
  });
}
async function remove(user_id: string, index: number) {
  if (!window.confirm("确定删除这条关系事件？")) return;
  await runAction("关系事件已删除", () => postJson("/api/grudge/remove", { user_id: Number(user_id), index }));
}
onMounted(load);
</script>

<style scoped>
.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}
</style>
